import { ColumnDef, PaginationState, SortingFn, SortingState, Row as TanstackRow } from '@tanstack/react-table';
import { get } from 'lodash';
import React from 'react';

import { DataTable } from '@frontend/app/refresh-components';
import BooleanCell from '@frontend/applications/Social/Analytics/components/table/columnDefs/BooleanCell';
import NumberCell from '@frontend/applications/Social/Analytics/components/table/columnDefs/NumberCell';
import Percentage from '@frontend/applications/Social/Analytics/components/table/columnDefs/Percentage';
import { InstagramCreator } from '@frontend/gql/social/graphql';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Tooltip, TooltipProvider, TooltipTrigger } from '@frontend/shadcn/components/ui/tooltip';
import { CircleInfoIcon, UpRightFromSquareIcon } from '@revfluence/fresh-icons/solid/esm';

import Generic from '../../../../Analytics/components/table/columnDefs/Generic';
import Skeleton from '../../../../Analytics/components/table/columnDefs/Skeleton';
import {
  TooltipContent as CustomTooltipContent,
  TooltipArrow,
  TooltipBody,
  TooltipHeader,
  TooltipItem,
  TooltipItemValue,
  TooltipTitle,
} from '../../../../components/tooltip';
import { getCountryLabel } from '../../../components/filters-form/utils';
import SocialMember from '../../../components/table/columnDefs/social-member';
import { ColumnDefinitionInput, RowType } from '../../../components/table/types';
import { AGE_RANGE_DISPLAY_MAP } from '../../types/instagram-creator-age-bucket';
import { GENDER_DISPLAY_NAME_MAP } from '../../types/instagram-creator-gender';
import { selectedColumnsToColumnDefInput } from './utils/selectedColumnsToColumnDef';

export interface MappedColumn {
  id: string;
  enableSorting: boolean;
  enableGlobalFilter: boolean;
  sortingFn: SortingFn<InstagramCreator>;
  meta: {
    greyBackground?: true;
    headerGroupBorder: {
      borderColor?: string;
    };
    onToggleClick: (() => void) | null;
    isColumnExpanded?: boolean;
    isExpandableColumn?: boolean;
    draggable?: boolean;
  };
  header: string;
  rowType: RowType;
  size?: number;
  minSize?: number;
  maxSize?: number;
  accessorFn?: (row) => unknown;
  isSubColumn?: boolean;
  tooltip?: string;
}

interface InstagramCreatorSearchTableProps {
  selectedColumns: ColumnDefinitionInput[];
  onSelectionChange: (rows: InstagramCreator[]) => void;
  globalFilter?: string | number;
  handleExpandColumn(columndId: string): void;
  // handleDragEnd(fromIndex: number, toIndex: number, mappedColumnData: MappedColumn[]): void;
  data: InstagramCreator[];
  paginationState: PaginationState;
  handleSortingChange: (sorting: SortingState) => void;
  sorting: SortingState;
  updatingColumns: string[];
  tableBodyTableRowProps?:
    | React.HTMLAttributes<HTMLTableRowElement>
    | ((row: TanstackRow<InstagramCreator>) => React.HTMLAttributes<HTMLTableRowElement>);
}

export const InstagramCreatorSearchTable: React.FC<InstagramCreatorSearchTableProps> = ({
  selectedColumns,
  onSelectionChange,
  handleExpandColumn,
  // handleDragEnd,
  data: instagramCreators,
  paginationState,
  handleSortingChange,
  sorting,
  globalFilter,
  updatingColumns,
  tableBodyTableRowProps,
}) => {
  const mappedColumns = selectedColumnsToColumnDefInput(selectedColumns, handleExpandColumn);
  const columns: ColumnDef<InstagramCreator>[] = mappedColumns.map((dataItem: MappedColumn) => {
    return {
      ...dataItem,
      header: () => {
        const tooltipContent = dataItem.tooltip;

        // if we have a tooltip content, we need to render a tooltip
        return (
          <div
            className="font-[system-ui] text-[#1F1F21] flex items-center gap-2"
            role="columnheader"
            aria-sort={
              sorting.find((s) => s.id === dataItem.id)?.desc
                ? 'descending'
                : sorting.find((s) => s.id === dataItem.id)
                  ? 'ascending'
                  : 'none'
            }
          >
            {dataItem.header}
            {tooltipContent && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <CircleInfoIcon
                      className="h-4 w-4 text-muted-foreground"
                      data-dd-action-name="member-table-header-tooltip-click"
                    />
                  </TooltipTrigger>
                  <CustomTooltipContent>
                    <TooltipHeader>
                      <TooltipTitle>{dataItem.header}</TooltipTitle>
                    </TooltipHeader>
                    <TooltipBody>
                      <TooltipItem>
                        <TooltipItemValue>{tooltipContent}</TooltipItemValue>
                      </TooltipItem>
                    </TooltipBody>
                    <TooltipArrow />
                  </CustomTooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        );
      },
      cell: ({ row }) => {
        if (updatingColumns.includes(dataItem.id)) {
          return (
            <div className="px-2 py-1">
              <Skeleton height="24px" />
            </div>
          );
        }
        return _renderRow(dataItem.rowType, row, dataItem);
      },
    };
  });

  const _renderRow = (type: RowType, row: TanstackRow<InstagramCreator>, data: MappedColumn) => {
    switch (type) {
      case RowType.SOCIAL_MEMBER: {
        return (
          <SocialMember
            name={row.original.username || ''}
            image={row.original.profilePictureUrl || ''}
            isVerified={row.original.isVerified}
            prefix="@"
          />
        );
      }
      case RowType.AGE_RANGE: {
        const ageBucketValue = row.original.ageBucket;
        const ageRangeValue = AGE_RANGE_DISPLAY_MAP[ageBucketValue];
        return <Generic value={ageRangeValue} aria-label={`${data.header}: ${ageRangeValue || 'Not available'}`} />;
      }
      case RowType.GENDER: {
        const genderValue = get(row.original, data.id);
        const genderDisplayValue = GENDER_DISPLAY_NAME_MAP[genderValue] || genderValue;
        return (
          <Generic value={genderDisplayValue} aria-label={`${data.header}: ${genderDisplayValue || 'Not available'}`} />
        );
      }
      case RowType.CAPITALIZED: {
        const value = row.original[data.id];
        return (
          <Generic
            value={value.toLowerCase()}
            className="capitalize"
            aria-label={`${data.header}: ${value || 'Not available'}`}
          />
        );
      }
      case RowType.COUNTRY: {
        const countryValue = get(row.original, data.id);
        const countryLabel = getCountryLabel(countryValue);
        return <Generic value={countryLabel} aria-label={`${data.header}: ${countryLabel || 'Not available'}`} />;
      }
      case RowType.NUMBER: {
        const numberValue = get(row.original, data.id);
        return (
          <NumberCell
            value={numberValue}
            aria-label={`${data.header}: ${numberValue || 'Not available'}`}
            align="left"
          />
        );
      }
      case RowType.PERCENTAGE: {
        const percentValue = get(row.original, data.id);
        return <Percentage value={percentValue} aria-label={`${data.header}: ${percentValue || 'Not available'}`} />;
      }
      case RowType.BOOLEAN: {
        const booleanValue = get(row.original, data.id);
        return <BooleanCell value={booleanValue} aria-label={`${data.header}: ${booleanValue || 'Not available'}`} />;
      }
      case RowType.INSTAGRAM_LINK: {
        const username = row.original.username;
        if (!username) {
          return <Generic value="" aria-label="Instagram link not available" />;
        }

        const instagramUrl = `https://www.instagram.com/${username}`;
        return (
          <div className="flex justify-center">
            <Button variant="outline" size="icon" className="h-6 w-6" asChild>
              <a
                href={instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`View ${username}'s Instagram profile`}
                onClick={(e) => e.stopPropagation()}
              >
                <UpRightFromSquareIcon className="h-4 w-4" />
              </a>
            </Button>
          </div>
        );
      }
      default:
        const genericValue = get(row.original, data.id);
        let genericStringValue = '';

        if (genericValue != null) {
          if (typeof genericValue === 'object') {
            try {
              genericStringValue = JSON.stringify(genericValue);
            } catch (e) {
              genericStringValue = '[Complex Object]';
            }
          } else {
            genericStringValue = String(genericValue);
          }
        }

        return (
          <Generic value={genericStringValue} aria-label={`${data.header}: ${genericStringValue || 'Not available'}`} />
        );
    }
  };

  return (
    <DataTable
      columns={columns}
      data={instagramCreators}
      paginationState={paginationState}
      onSortingChange={(updaterOrValue) => {
        const newSorting = typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;
        handleSortingChange(newSorting);
      }}
      sorting={sorting}
      globalFilter={globalFilter}
      wrapperClassName="bg-primary-foreground"
      maxHeight="calc(100vh - 110px)"
      onSelectionChange={onSelectionChange}
      bordered
      sortable
      selectable
      tableBodyTableRowProps={tableBodyTableRowProps}
    />
  );
};
